import { WbsListDto } from '@api/client';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import { wbsDataSelector, wbsDataTableSelector } from 'pages/private/_redux/planning/selectors';
import { getWbsProjectPlanningById, saveWbsDataTable } from 'pages/private/_redux/planning/actions';

const useGetWbsTableData = (projectId: number) => {
  const dispatch = useAppDispatch();
  const deliverables = useAppSelector(wbsDataSelector);
  const deliverablesDataTableFromStore = useAppSelector(wbsDataTableSelector);
  const [deliverablesTableData, setDeliverablesTableData] = useState<WbsListDto[] | undefined>(
    deliverablesDataTableFromStore
  );
  const [deliverablesTableDataShown, setDeliverablesTableDataShown] = useState<WbsListDto[] | undefined>(
    deliverablesDataTableFromStore
  );
  const [totalForecastDeliverables, setTotalForecastDeliverables] = useState<number[]>([]);
  const [years, setYears] = useState<Array<number | undefined>>([]);
  const [currentYear, setCurrentYear] = useState<number | 'all'>('all');

  useEffect(() => {
    if (!deliverables) dispatch(getWbsProjectPlanningById(projectId));
  }, []);

  // inizializza la tabella
  useEffect(() => {
    if (!deliverablesTableData) {
      const mappedDeliverables = deliverables?.map((el) => ({
        id: el.id || -Math.floor(Math.random() * 100000),
        titolo: el.titolo,
        pesoAttivita: el.pesoAttivita,
        projectId: el.projectId,
        corrispettivoEconomico: el.corrispettivoEconomico,
        wbsPlanningDtoList: el.wbsPlanningDtoList
          ?.map((el) => ({
            id: el.id || -Math.floor(Math.random() * 100000),
            mese: el.mese,
            anno: el.anno,
            corrispettivo: el.corrispettivo,
            enable: el.enable,
          }))
          .sort((a, b) => {
            // Ordina per anno
            if (a.anno && b.anno && a.anno !== b.anno) {
              return a.anno - b.anno;
            }
            // Se gli anni sono uguali, ordina per mese
            return (a.mese ?? 0) - (b.mese ?? 0);
          }),
      }));
      setDeliverablesTableData(mappedDeliverables);
      setDeliverablesTableDataShown(mappedDeliverables);

      const yearsToSet: Array<number | undefined> = [];

      mappedDeliverables?.[0]?.wbsPlanningDtoList?.forEach((alloc) => {
        if (!yearsToSet.includes(alloc?.anno)) {
          yearsToSet.push(alloc?.anno);
        }
      });
      setYears(yearsToSet);
    }
  }, [deliverables]);

  useEffect(() => {
    calculateTotalForecastDeliverables();
    dispatch(saveWbsDataTable(deliverablesTableData));
  }, [deliverablesTableData]);

  const calculateTotalForecastDeliverables = () => {
    const totalForecastDeliverables: number[] = [];
    deliverablesTableData?.forEach((allocation) => {
      let totalGU = 0;
      allocation.wbsPlanningDtoList?.forEach((alloc) => {
        if (alloc.enable) {
          totalGU += alloc.corrispettivo ?? 0;
        }
      });
      totalForecastDeliverables.push(totalGU);
    });
    setTotalForecastDeliverables(totalForecastDeliverables);
  };

  const filterAllocationsByYear = (
    year: number | 'all',
    deliverablesData: WbsListDto[] | undefined = deliverablesTableData
  ): void => {
    setCurrentYear(year);
    if (year === 'all') {
      setDeliverablesTableDataShown(deliverablesData);
    } else {
      const filteredAllocations = deliverablesData?.map((el) => ({
        ...el,
        wbsPlanningDtoList: el.wbsPlanningDtoList?.filter((alloc) => alloc.anno === year),
      }));
      setDeliverablesTableDataShown(filteredAllocations);
    }
  };

  return {
    deliverablesTableData,
    deliverablesTableDataShown,
    years,
    currentYear,
    totalForecastDeliverables,
    filterAllocationsByYear,
    setDeliverablesTableData,
    setTotalForecastDeliverables,
  };
};

export default useGetWbsTableData;
